import React, { useEffect, useState } from 'react';
import { useQuill } from 'react-quilljs';
import Quill from 'quill';
import PropTypes from 'prop-types';
import './quill.snow.css';
import './MessageInputEditor.scss';
import T from 'i18n-react';
import { useSelector } from 'react-redux';
import sendIcon from '../../images/sendIcon.svg';

// Override Quill's default link icon with Material Design chain-link icon
const icons = Quill.import('ui/icons');
icons['link'] = '<svg viewBox="0 0 24 24"><path class="ql-fill" d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>';

const MessageInputEditor = ({ onInputChanged, messageContent, disabled, visible, onBlur, onSend }) => {

    const layoutType = useSelector(state => (state.layout.layoutType));
    
    const [quillInitialized, setQuillInitialized] = useState(false);

    const theme = 'snow';
    const limit = 10 * 1024;

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link']
        ],
    }

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'indent',
        'link'
    ]

    const placeholder = T.translate("Messages.Labels.WriteMessage");

    const { quill, quillRef } = useQuill({ theme, modules, formats, placeholder });

    useEffect(() => {
        // since we aren't using this as a regular input
        // we only care if the content has been cleared by the parent.
        // In this case, clear the input, but we can safely ignore all other updates.
        if (!messageContent && !!quill) {
            quill.setText('')
        }
    }, [messageContent])

    useEffect(() => {
        // this would be better if it could be css, but that's not an option here.
        if (quill) {
            if (disabled) {
                quill.disable()
            } else {
                quill.enable()
            }
        }
    }, [disabled])

    useEffect(() => {
        // set up event handlers for the quill editor
        if (quill && !quillInitialized) {
            setQuillInitialized(true)

            quill.on('text-change', () => {
                if (quill.getLength() > limit) {
                    quill.deleteText(limit, quill.getLength());
                }
                onInputChanged(quillRef.current.firstChild.innerHTML);
            });

            quill.root.addEventListener('blur', () => {
                if (onBlur) {
                    onBlur();
                }
            });
        }
    }, [quill]);

    useEffect(() => {
        if (quillInitialized) {
            let editor_elements = document.getElementsByClassName('ql-editor')
            if (editor_elements && editor_elements[0]) {
                editor_elements[0].classList.toggle('custom-scroll');
            }
            //For POM message prefill
            if (messageContent?.length > 0) {
                quill.setContents(quill.clipboard.convert(messageContent))
            }
            else {
                quill.setText('')
            }
            if (!visible) {
                quill.setText('')
            }
        }
    }, [quillInitialized])

    return (<div className="quill mb-1">
        <div ref={quillRef} id='quill-input' className={layoutType !== 'desktop' ? layoutType + " custom-scroll" : "custom-scroll"} />
        {onSend && (
            <button 
                className="quill-send-btn" 
                onClick={onSend} 
                disabled={disabled}
                aria-label={T.translate("Messages.Labels.SendMessage")}
                type="button"
            >
                <img src={sendIcon} alt="" width="24" height="24" />
            </button>
        )}
    </div>)
}

MessageInputEditor.propTypes = {
    onInputChanged: PropTypes.func.isRequired,
    messageContent: PropTypes.string,
    disabled: PropTypes.bool,
    onBlur: PropTypes.func,
    onSend: PropTypes.func
}

export default MessageInputEditor